import { useCallback } from "react"
import { graphql, UseMutationConfig } from "react-relay"
import { type CustomMutationParams, useMutationWithErrorDialog } from "@/hooks/useMutationWithErrorDialog"
import { formatDiagnosisCodeText } from "@/modules/patients/components/diagnosis-code"
import { generateActiveAndInactiveMedicationList } from "@/modules/patients/detail/medication/hooks/useMedications"
import { usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useChGenerateNutritionalCounsellingDocumentMutation as useChGenerateNutritionalCounsellingDocumentMutationType } from "@/modules/patients/detail/therapy-prescription/ch/services/__generated__/useChGenerateNutritionalCounsellingDocumentMutation.graphql"
import { getDiagnosisCodeByDiagnosisCode } from "@/modules/patients/detail/therapy-prescription/ch/services/getDiagnosisCodeByDiagnosisCode"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generateNutritionalCounsellingDocumentMutation = graphql`
  mutation useChGenerateNutritionalCounsellingDocumentMutation(
    $input: Ch_generateNutritionalPrescriptionDocumentInput!
  ) {
    ch_generateNutritionalPrescriptionDocument(input: $input) {
      ch_NutritionalPrescription {
        id
        fileId
        ...chNutritionalCounsellingFragment
      }
      errors {
        ... on Error {
          message
        }
      }
    }
  }
`
type Config = Omit<UseMutationConfig<useChGenerateNutritionalCounsellingDocumentMutationType>, "variables"> & {
  id: string
  diagnosisCode: string
  medicationIds: string[]
}

const useChGenerateNutritionalCounsellingDocumentMutation = (params?: CustomMutationParams) => {
  const [commit, isFlight] = useMutationWithErrorDialog<useChGenerateNutritionalCounsellingDocumentMutationType>(
    generateNutritionalCounsellingDocumentMutation,
    params,
  )
  const patientById = usePatientParam()
  const patientData = usePatientFileContext((state) => state.patientData)

  const commitHandler = useCallback(
    async (config: Config) => {
      const { id, medicationIds, diagnosisCode } = config
      const { activeMedications, inactiveMedications } = generateActiveAndInactiveMedicationList(
        patientData?.medications?.edges ?? [],
        "CH",
      )
      const medications = [...activeMedications, ...inactiveMedications]
        ?.filter((medication) => medicationIds?.includes(medication.id))
        .map((item) => item.name)

      const { code, description } = await getDiagnosisCodeByDiagnosisCode(diagnosisCode)
      const formattedDiagnosisCode = formatDiagnosisCodeText(code, description)

      commit({
        variables: {
          input: {
            id,
            patientId: patientById,
            diagnosisCode: formattedDiagnosisCode,
            medications,
          },
        },
        ...config,
      })
    },
    [commit, patientById, patientData?.medications?.edges],
  )

  return [commitHandler, isFlight] as const
}

export { useChGenerateNutritionalCounsellingDocumentMutation }
